import { useEffect, useMemo, useState } from 'react';
import {
  Modal, Stack, Group, Button, SegmentedControl, Select, NumberInput, Alert, Text, Paper, Table, ActionIcon, TextInput,
} from '@mantine/core';
import { IconAdd as IconPlus, IconChip as IconTrash } from '../../components/icons/CncIcons';
import { api } from '../../api/client';
import type { QuotationProcess } from './NewQuotationModal';
import type { Technician } from '../technicians/TechniciansPage';

type EntryType = 'Equipment' | 'Labour' | 'Consumable';

export interface ProcessForm {
  entryType: EntryType;
  equipmentId?: number | null;
  technicianId?: number | null;
  consumableId?: number | null;
  labourDescription?: string | null;
  mainQuantity: number;
  markupPct: number;
  accessories: Array<{ accessoryId: number; hours: number }>;
  consumables: Array<{ consumableId: number; quantity: number }>;
}

interface EquipmentLite { id: number; name: string; markupPct: number; accessories?: Array<{ id: number; name: string }> }
interface ConsumableLite { id: number; name: string; markupPct: number }

export default function AddProcessModal({
  orderId,
  quotationId,
  quotationPartId,
  initial,
  onClose,
  onDone,
}: {
  orderId: number;
  quotationId: number;
  quotationPartId: number;
  initial: QuotationProcess | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const [entryType, setEntryType] = useState<EntryType>(initial?.entryType ?? 'Equipment');

  const [equipment, setEquipment] = useState<EquipmentLite[]>([]);
  const [consumables, setConsumables] = useState<ConsumableLite[]>([]);
  const [technicians, setTechnicians] = useState<Technician[]>([]);
  const [equipmentDetail, setEquipmentDetail] = useState<any | null>(null);

  const [equipmentId, setEquipmentId] = useState<string | null>(initial?.equipmentId ? String(initial.equipmentId) : null);
  const [technicianId, setTechnicianId] = useState<string | null>(initial?.technicianId ? String(initial.technicianId) : null);
  const [consumableId, setConsumableId] = useState<string | null>(initial?.consumableId ? String(initial.consumableId) : null);
  const [mainQuantity, setMainQuantity] = useState<number | string>(initial?.mainQuantity ?? 1);
  const [markupPct, setMarkupPct] = useState<number | string>(initial?.markupPct ?? (initial?.entryType === 'Labour' ? 0 : 30));
  const [labourDescription, setLabourDescription] = useState<string>(initial?.labourDescription ?? '');

  const [accRows, setAccRows] = useState<Array<{ accessoryId: number | null; hours: number }>>(
    initial?.accessories?.map((a) => ({ accessoryId: a.accessoryId, hours: a.hours })) ?? []
  );
  const [conRows, setConRows] = useState<Array<{ consumableId: number | null; quantity: number }>>(
    initial?.consumables?.map((c) => ({ consumableId: c.consumableId, quantity: c.quantity })) ?? []
  );

  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get<EquipmentLite[]>('/equipment').then(setEquipment);
    api.get<ConsumableLite[]>('/consumables').then(setConsumables);
    api.get<Technician[]>('/technicians').then(setTechnicians);
  }, []);

  useEffect(() => {
    if (entryType !== 'Equipment' || !equipmentId) { setEquipmentDetail(null); return; }
    api.get<any>(`/equipment/${equipmentId}`).then(setEquipmentDetail).catch(() => setEquipmentDetail(null));
  }, [equipmentId, entryType]);

  // When entry type changes, suggest a default markup based on the selected entity.
  // Labour has no natural markup default, so zero.
  useEffect(() => {
    if (initial) return;
    if (entryType === 'Equipment' && equipmentId) {
      const e = equipment.find((x) => String(x.id) === equipmentId);
      if (e) setMarkupPct(e.markupPct);
    } else if (entryType === 'Consumable' && consumableId) {
      const c = consumables.find((x) => String(x.id) === consumableId);
      if (c) setMarkupPct(c.markupPct);
    } else if (entryType === 'Labour') {
      setMarkupPct(0);
    } else {
      setMarkupPct(30);
    }
  }, [entryType, equipmentId, consumableId, equipment, consumables, initial]);

  const accessoryOptions = useMemo(() => {
    return (equipmentDetail?.accessories ?? []).map((a: any) => ({ value: String(a.id), label: a.name }));
  }, [equipmentDetail]);

  // For the per-equipment consumable picker: typical consumables (attached to the equipment)
  // are shown first under a header, other consumables under a second header.
  // Mantine's grouped Select keeps the group sections visually separated.
  const equipmentConsumableOptions = useMemo(() => {
    const typicalIds = new Set<number>((equipmentDetail?.consumables ?? []).map((c: any) => c.id));
    const byName = (a: ConsumableLite, b: ConsumableLite) => a.name.localeCompare(b.name);
    const typical = consumables.filter((c) => typicalIds.has(c.id)).sort(byName);
    const other = consumables.filter((c) => !typicalIds.has(c.id)).sort(byName);
    const groups: Array<{ group: string; items: Array<{ value: string; label: string }> }> = [];
    if (typical.length > 0) {
      groups.push({
        group: 'Typical for this equipment',
        items: typical.map((c) => ({ value: String(c.id), label: `★ ${c.name}` })),
      });
    }
    if (other.length > 0) {
      groups.push({
        group: 'Other consumables',
        items: other.map((c) => ({ value: String(c.id), label: c.name })),
      });
    }
    return groups;
  }, [equipmentDetail, consumables]);

  async function save() {
    setErr(null);
    const main = Math.max(0, Number(mainQuantity) || 0);
    const markup = Math.min(200, Math.max(0, Math.round(Number(markupPct) || 0)));
    let body: ProcessForm = {
      entryType,
      mainQuantity: main,
      markupPct: markup,
      accessories: [],
      consumables: [],
    };
    if (entryType === 'Equipment') {
      if (!equipmentId) return setErr('Pick equipment.');
      body.equipmentId = Number(equipmentId);
      body.accessories = accRows.filter((r) => r.accessoryId).map((r) => ({ accessoryId: r.accessoryId!, hours: Math.max(0, r.hours) }));
      body.consumables = conRows.filter((r) => r.consumableId).map((r) => ({ consumableId: r.consumableId!, quantity: Math.max(0, r.quantity) }));
    } else if (entryType === 'Labour') {
      if (!technicianId) return setErr('Pick a technician.');
      body.technicianId = Number(technicianId);
      body.labourDescription = labourDescription.trim().slice(0, 100) || null;
    } else if (entryType === 'Consumable') {
      if (!consumableId) return setErr('Pick a consumable.');
      body.consumableId = Number(consumableId);
    }
    setBusy(true);
    try {
      const base = `/orders/${orderId}/quotations/${quotationId}/parts/${quotationPartId}/processes`;
      if (initial) await api.put(`${base}/${initial.id}`, body);
      else await api.post(base, body);
      onDone();
    } catch (e: any) { setErr(e.message ?? 'Save failed'); }
    finally { setBusy(false); }
  }

  return (
    <Modal opened onClose={onClose} size="lg" title={<span className="modal-title-custom">{initial ? 'Edit Process' : 'Add Process'}</span>}>
      <Stack>
        <SegmentedControl
          data-tour="proc-type"
          data={[
            { value: 'Equipment', label: 'Equipment' },
            { value: 'Labour', label: 'Labour' },
            { value: 'Consumable', label: 'Consumable' },
          ]}
          value={entryType}
          onChange={(v) => setEntryType(v as EntryType)}
        />

        {entryType === 'Equipment' && (
          <Stack>
            <Select
              label="Equipment"
              placeholder="Select equipment"
              data-tour="proc-equipment"
              data={equipment.map((e) => ({ value: String(e.id), label: e.name }))}
              value={equipmentId}
              onChange={setEquipmentId}
              searchable
            />
            <NumberInput label="Hours on equipment" data-tour="proc-hours" value={mainQuantity} onChange={setMainQuantity} min={0} step={0.25} decimalScale={2} />
            {equipmentDetail && (
              <>
                <Paper withBorder>
                  <Table>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Accessory</Table.Th>
                        <Table.Th style={{ width: 140 }}>Hours</Table.Th>
                        <Table.Th style={{ width: 40 }}></Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {accRows.map((row, i) => (
                        <Table.Tr key={i}>
                          <Table.Td>
                            <Select
                              placeholder="Accessory"
                              data={accessoryOptions}
                              value={row.accessoryId ? String(row.accessoryId) : null}
                              onChange={(v) => {
                                const n = [...accRows]; n[i] = { ...row, accessoryId: v ? Number(v) : null }; setAccRows(n);
                              }}
                            />
                          </Table.Td>
                          <Table.Td>
                            <NumberInput value={row.hours} onChange={(v) => { const n = [...accRows]; n[i] = { ...row, hours: Number(v) || 0 }; setAccRows(n); }} min={0} step={0.25} decimalScale={2} />
                          </Table.Td>
                          <Table.Td>
                            <ActionIcon variant="subtle" color="red" onClick={() => setAccRows(accRows.filter((_, j) => j !== i))}>
                              <IconTrash size={14} />
                            </ActionIcon>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                      <Table.Tr>
                        <Table.Td colSpan={3}>
                          <Button fullWidth variant="light" leftSection={<IconPlus size={14} />} onClick={() => setAccRows([...accRows, { accessoryId: null, hours: 0 }])}>
                            Add accessory
                          </Button>
                        </Table.Td>
                      </Table.Tr>
                    </Table.Tbody>
                  </Table>
                </Paper>
                <Paper withBorder>
                  <Table>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Consumable</Table.Th>
                        <Table.Th style={{ width: 140 }}>Quantity</Table.Th>
                        <Table.Th style={{ width: 40 }}></Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {conRows.map((row, i) => (
                        <Table.Tr key={i}>
                          <Table.Td>
                            <Select
                              placeholder="Consumable"
                              data={equipmentConsumableOptions}
                              value={row.consumableId ? String(row.consumableId) : null}
                              onChange={(v) => { const n = [...conRows]; n[i] = { ...row, consumableId: v ? Number(v) : null }; setConRows(n); }}
                              searchable
                              description={
                                equipmentConsumableOptions.length === 2
                                  ? 'Typical consumables (★) for this equipment are listed first; other consumables follow.'
                                  : equipmentConsumableOptions.length === 1 && equipmentConsumableOptions[0].group === 'Typical for this equipment'
                                    ? 'Only typical consumables are available for this equipment.'
                                    : undefined
                              }
                            />
                          </Table.Td>
                          <Table.Td>
                            <NumberInput value={row.quantity} onChange={(v) => { const n = [...conRows]; n[i] = { ...row, quantity: Number(v) || 0 }; setConRows(n); }} min={0} step={1} decimalScale={2} />
                          </Table.Td>
                          <Table.Td>
                            <ActionIcon variant="subtle" color="red" onClick={() => setConRows(conRows.filter((_, j) => j !== i))}>
                              <IconTrash size={14} />
                            </ActionIcon>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                      <Table.Tr>
                        <Table.Td colSpan={3}>
                          <Button fullWidth variant="light" leftSection={<IconPlus size={14} />} onClick={() => setConRows([...conRows, { consumableId: null, quantity: 1 }])}>
                            Add consumable
                          </Button>
                        </Table.Td>
                      </Table.Tr>
                    </Table.Tbody>
                  </Table>
                </Paper>
              </>
            )}
          </Stack>
        )}

        {entryType === 'Labour' && (
          <Stack>
            <Select
              label="Lab Technician"
              placeholder="Select technician"
              data-tour="proc-technician"
              data={technicians.map((t) => ({ value: String(t.id), label: t.name }))}
              value={technicianId}
              onChange={setTechnicianId}
              searchable
            />
            <NumberInput label="Hours" data-tour="proc-tech-hours" value={mainQuantity} onChange={setMainQuantity} min={0} step={0.25} decimalScale={2} />
            <TextInput
              label="Description of Technician Activity"
              placeholder="e.g. CAM programming + first article inspection"
              value={labourDescription}
              onChange={(e) => setLabourDescription(e.currentTarget.value)}
              maxLength={100}
              description={`${labourDescription.length}/100`}
            />
          </Stack>
        )}

        {entryType === 'Consumable' && (
          <Stack>
            <Select
              label="Consumable"
              placeholder="Select consumable"
              data-tour="proc-consumable"
              data={consumables.map((c) => ({ value: String(c.id), label: c.name }))}
              value={consumableId}
              onChange={setConsumableId}
              searchable
            />
            <NumberInput label="Quantity" value={mainQuantity} onChange={setMainQuantity} min={0} step={1} decimalScale={2} />
          </Stack>
        )}

        <NumberInput
          label="Markup %"
          data-tour="proc-markup"
          value={markupPct}
          onChange={setMarkupPct}
          min={0}
          max={200}
          step={5}
          description="Default pulled from the selected equipment/consumable; editable per process and also via the slider in the quotation row."
        />

        {err && <Alert color="red">{err}</Alert>}
        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button data-tour="proc-save" onClick={save} loading={busy}>Save</Button>
        </Group>
        <Text size="xs" c="dimmed">Cost is computed from the raw inputs (power + depreciation for equipment, typical cost for consumables, hourly rate for labour). Price = Cost × (1 + Markup %).</Text>
      </Stack>
    </Modal>
  );
}
